import { useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import type { ContentPageSortField } from '@xing-com/crate-common-graphql-types';

import { INITIAL_SORTING_ITEM } from '../components/cards-sorting/sorting-items';
import type { RecommendationPageType } from '../config';
import { contentPageTypeMap } from '../config';
import type { GetRecommendationsByTypeQuery } from '../graphql/get-recommendations-by-type.gql-types';
import { GetRecommendationsByTypeDocument } from '../graphql/get-recommendations-by-type.gql-types';
import { getSortOrder } from '../helpers/hooks';
import { getRecommendationCardItems } from '../models/recommendation-card';
import type { RecommendationCardItem } from '../types/types';

type UseRecommendationCardsReturnType = {
  isLoading: boolean;
  isLoadingMore: boolean;
  hasError: boolean;
  hasLoadMoreButton: boolean;
  recommendationCardItems: RecommendationCardItem[];
  sortCards: (contentPageSortField: ContentPageSortField) => void;
  refetchRecommendations: () => void;
  loadMore: () => void;
};

const INITIAL_PAGE_SIZE = 24;

export const useRecommendationCards = (
  type: RecommendationPageType
): UseRecommendationCardsReturnType => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { loading, error, data, refetch, fetchMore } = useQuery(
    GetRecommendationsByTypeDocument,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        pageType: contentPageTypeMap[type],
        sortField: INITIAL_SORTING_ITEM,
        sortOrder: getSortOrder(INITIAL_SORTING_ITEM),
        first: INITIAL_PAGE_SIZE,
      },
    }
  );

  const refetchRecommendations = (): void => {
    refetch();
  };

  const sortCards = useCallback(
    (contentPageSortField: ContentPageSortField): void => {
      refetch({
        sortField: contentPageSortField,
        sortOrder: getSortOrder(contentPageSortField),
      });
    },
    [refetch]
  );

  const loadMore = useCallback((): void => {
    setIsLoadingMore(true);

    fetchMore({
      variables: {
        after: data?.contentPagesNewsSourcesByType?.pageInfo.endCursor,
      },
      updateQuery: (
        prev: GetRecommendationsByTypeQuery,
        { fetchMoreResult }
      ): GetRecommendationsByTypeQuery => {
        if (!fetchMoreResult?.contentPagesNewsSourcesByType) return prev;

        return {
          contentPagesNewsSourcesByType: {
            ...fetchMoreResult.contentPagesNewsSourcesByType,
            edges: [
              ...(prev?.contentPagesNewsSourcesByType?.edges ?? []),
              ...fetchMoreResult.contentPagesNewsSourcesByType.edges,
            ],
          },
        };
      },
    }).finally(() => {
      setIsLoadingMore(false);
    });
  }, [data, fetchMore]);

  const recommendationCardItems = useMemo(
    () => getRecommendationCardItems(type, data),
    [type, data]
  );

  return {
    recommendationCardItems,
    isLoading: loading && !isLoadingMore,
    isLoadingMore,
    hasError: !!error,
    hasLoadMoreButton:
      !!data?.contentPagesNewsSourcesByType?.pageInfo.hasNextPage,
    refetchRecommendations,
    sortCards,
    loadMore,
  };
};
